import { Mic, MonitorSpeaker, Power, RefreshCw } from 'lucide-react';
import { LiveAudioWaveVisualizer } from '@/components/session-call/LiveAudioWaveVisualizer';
import { cn } from '@/lib/utils';

type P2PTranscriptionAudioCaptureDisplayProps = {
	micStream: MediaStream | null;
	systemStream: MediaStream | null;
	previewMicStream: MediaStream | null;
	isActive: boolean;
	isSystemActivating?: boolean;
	onReplaceSystemAudio: () => void;
	onRemoveSystemAudio: () => void;
	onEnableSystemAudio: () => void;
};

const P2PTranscriptionAudioCaptureDisplay = ({
	micStream,
	systemStream,
	previewMicStream,
	isActive,
	isSystemActivating = false,
	onReplaceSystemAudio,
	onRemoveSystemAudio,
	onEnableSystemAudio,
}: P2PTranscriptionAudioCaptureDisplayProps) => {
	const displayMicStream = micStream ?? previewMicStream;
	const isSystemActive = !!systemStream;

	return (
		<div className="grid w-full max-w-lg grid-cols-2 gap-y-2 overflow-hidden rounded-2xl border border-white/[0.06] bg-[#1a2233]/50 backdrop-blur-sm">
			<div className="flex min-w-0 items-center gap-2 self-stretch border-r border-white/[0.06] px-3 pt-3 text-left text-xs font-medium text-zinc-400">
				<Mic
					className="size-3.5 shrink-0 text-white/70"
					strokeWidth={2.25}
				/>
				Microfone
			</div>

			<div className="flex min-w-0 items-center justify-between gap-2 self-stretch px-3 pt-3">
				<div className="flex min-w-0 items-center gap-2 text-left text-xs font-medium text-zinc-400">
					<MonitorSpeaker
						className="size-3.5 shrink-0 text-white/70"
						strokeWidth={2.25}
					/>
					<span className="truncate">Áudio do computador</span>
				</div>
				<div className="flex shrink-0 items-center gap-0.5">
					{isSystemActive ? (
						<>
							<button
								type="button"
								onClick={onReplaceSystemAudio}
								disabled={isSystemActivating}
								className="rounded-full p-1.5 text-zinc-500 transition-colors hover:bg-white/10 hover:text-white disabled:opacity-50"
								aria-label="Trocar fonte de áudio do computador"
								title="Trocar fonte"
							>
								<RefreshCw
									className="size-3.5"
									strokeWidth={2.25}
								/>
							</button>
							<button
								type="button"
								onClick={onRemoveSystemAudio}
								disabled={isSystemActivating}
								className="rounded-full p-1.5 text-zinc-500 transition-colors hover:bg-white/10 hover:text-white disabled:opacity-50"
								aria-label="Desativar áudio do computador"
								title="Desativar"
							>
								<Power
									className="size-3.5"
									strokeWidth={2.25}
								/>
							</button>
						</>
					) : (
						<button
							type="button"
							onClick={onEnableSystemAudio}
							disabled={isSystemActivating}
							className={cn(
								'rounded-full px-2.5 py-1 text-[10px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50',
								isSystemActivating
									? 'bg-[#38bdf8]/20 text-[#7dd3fc]'
									: 'bg-white/10 text-zinc-300 hover:bg-white/15 hover:text-white',
							)}
						>
							{isSystemActivating ? 'Ativando...' : 'Ativar'}
						</button>
					)}
				</div>
			</div>

			<div className="flex min-h-10 min-w-0 items-center self-stretch border-r border-white/[0.06] px-3 pb-3">
				<LiveAudioWaveVisualizer
					stream={displayMicStream}
					active={isActive}
					compact
					className="h-full w-full"
					ariaLabel="Áudio do microfone"
				/>
			</div>

			<div className="flex min-h-10 min-w-0 items-center self-stretch px-3 pb-3">
				<LiveAudioWaveVisualizer
					stream={systemStream}
					active={isActive && isSystemActive}
					inactive={!isSystemActive}
					compact
					className="h-full w-full"
					ariaLabel="Áudio do computador"
				/>
			</div>
		</div>
	);
};

export { P2PTranscriptionAudioCaptureDisplay };
